import { useEffect, useState } from "react";
import { X, Loader2, Check, Ban } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { fmtD, newRef } from "../lib/units";
import { ExportBar } from "../lib/printdoc";

export const PAY_MODES = ["cash", "upi", "bank", "cheque", "card", "other"];
const TYPE_LABEL = { room_charge: "Room night", restaurant: "Restaurant bill", hall_charge: "Hall charge", other_charge: "Charge", cancel_fee: "Cancellation fee", payment: "Payment", advance: "Advance", discount: "Discount", refund: "Refund", round_off: "Round off" };

export function Modal({ title, overline, onClose, children, testid, wide, xwide }) {
  return (
    <div className="modal-backdrop"><div className={xwide ? "modal recipe-modal modal-xwide" : wide ? "modal recipe-modal" : "modal"} data-testid={testid}>
      <div className="modal-head"><div><span className="overline">{overline}</span><h2>{title}</h2></div><button className="icon-button" onClick={onClose} data-testid={`${testid}-close`}><X size={18} /></button></div>
      {children}
    </div></div>
  );
}

export function useForm(init) {
  const [f, setF] = useState(init);
  return [f, (k, v) => setF((x) => ({ ...x, [k]: v })), setF];
}

export function Submit({ busy, label, testid }) {
  return <button className="primary-button full" disabled={busy} data-testid={testid}>{busy ? <Loader2 className="spin" size={14} /> : <Check size={14} />} {label}</button>;
}

export function useAction() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const run = async (fn) => { setBusy(true); setErr(""); try { return await fn(); } catch (e) { setErr(errorMessage(e)); return null; } finally { setBusy(false); } };
  return { busy, err, run, setErr };
}

export function Err({ err, testid }) { return err ? <div className="auth-alert error" data-testid={testid}>{err}</div> : null; }

function EntryForm({ postUrl, onDone, settled }) {
  const [f, set, setF] = useForm({ type: settled ? "refund" : "payment", amount: "", mode: "cash", ref_no: "", description: "", gst_rate: 0 });
  const [ref, setRef] = useState(newRef);
  const a = useAction();
  const submit = (e) => { e.preventDefault(); a.run(async () => { await api.post(postUrl, { ...f, amount: Number(f.amount), gst_rate: Number(f.gst_rate), client_ref: ref }); setRef(newRef()); setF((x) => ({ ...x, amount: "", ref_no: "", description: "" })); onDone(); }); };
  const paid = ["payment", "advance", "refund"].includes(f.type);
  return (
    <form onSubmit={submit} className="panel" style={{ padding: 12 }} data-testid="folio-entry-form">
      <div className="form-grid">
        <label>Entry<select value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="folio-entry-type">
          {!settled && <><option value="payment">Payment</option><option value="advance">Advance</option><option value="other_charge">Extra charge</option><option value="discount">Discount</option></>}<option value="refund">Refund</option></select></label>
        <label>Amount (₹)<input type="number" step="0.01" min="0.01" required value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="folio-entry-amount" /></label>
        {paid && <label>Mode<select value={f.mode} onChange={(e) => set("mode", e.target.value)} data-testid="folio-entry-mode">{PAY_MODES.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}</select></label>}
        {f.type === "other_charge" && <label>GST %<select value={f.gst_rate} onChange={(e) => set("gst_rate", e.target.value)}>{[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}</select></label>}
        <label>Reference<input value={f.ref_no} onChange={(e) => set("ref_no", e.target.value)} data-testid="folio-entry-ref" /></label>
        <label>Description{["payment", "advance"].includes(f.type) ? "" : " (required)"}<input value={f.description} required={!["payment", "advance"].includes(f.type)} onChange={(e) => set("description", e.target.value)} data-testid="folio-entry-desc" /></label>
      </div>
      <Err err={a.err} testid="folio-entry-error" />
      <Submit busy={a.busy} label="Post to folio" testid="folio-entry-save" />
    </form>
  );
}

const FOLIO_COLS = [{ key: "date", label: "Date", type: "date" }, { key: "entry", label: "Entry" }, { key: "details", label: "Details" }, { key: "amount", label: "Amount", type: "money" },
  { key: "tax", label: "GST", type: "money" }, { key: "signed", label: "Total (± )", type: "money" }, { key: "status", label: "Status" }];

function folioDoc(doc) {
  const s = doc.summary || {}, who = doc.guest?.name || doc.customer?.name || doc.customer_name || "";
  const live = (e) => e.status !== "voided";
  return {
    title: `Guest folio ${doc.ref || ""}`.trim(), outlet: who, filename: `Folio_${doc.ref || "booking"}`,
    period: [doc.room_number && `Room ${doc.room_number}`, doc.check_in && `${fmtD(doc.check_in)} → ${fmtD(doc.check_out)}`, doc.event_date && `Event ${fmtD(doc.event_date)}`, doc.status].filter(Boolean).join(" · "),
    sections: [
      { heading: "Folio entries", columns: FOLIO_COLS, rows: (doc.folio || []).map((e) => ({ date: e.date, entry: TYPE_LABEL[e.type] || e.type, details: [e.description, e.mode?.toUpperCase(), e.ref_no].filter(Boolean).join(" · "),
        amount: e.amount, tax: e.tax || 0, signed: live(e) ? (["payment", "advance", "discount"].includes(e.type) ? -e.total : e.total) : 0, status: e.status || "posted" })) },
      { heading: "Summary", columns: [{ key: "label", label: "Particulars" }, { key: "amount", label: "Amount (₹)", type: "money" }], rows: [
        { label: s.contract_total != null ? "Contract (incl. GST)" : s.stay_total != null ? "Stay total (incl. GST)" : "Charges", amount: s.contract_total ?? s.stay_total ?? s.charges }, { label: "Paid", amount: s.payments },
        { label: "Refunds", amount: s.refunds }, { label: "Discounts", amount: s.discounts }, { label: "Balance due", amount: s.balance, _bold: true }] },
    ],
  };
}

export function Folio({ doc, postUrl, onChanged }) {
  const [adding, setAdding] = useState(false);
  const a = useAction();
  const settled = ["checked_out", "completed"].includes(doc.status);
  const voidIt = (e) => { const reason = window.prompt("Reason for voiding?"); if (reason) a.run(async () => { await api.post(`/folio-entries/${e.id}/void`, { reason }); onChanged(); }); };
  const s = doc.summary || {};
  return (
    <div data-testid="folio">
      <div className="table-wrap" style={{ maxHeight: 300, overflow: "auto", maxWidth: "100%" }}><table data-testid="folio-table" style={{ minWidth: 640 }}><thead><tr><th>Date</th><th>Entry</th><th>Details</th><th>Amount</th><th>GST</th><th>Total</th><th></th></tr></thead><tbody>
        {(doc.folio || []).map((e) => (
          <tr key={e.id} style={e.status === "voided" ? { opacity: 0.45, textDecoration: "line-through" } : undefined} data-testid={`folio-row-${e.id}`}>
            <td>{fmtD(e.date)}</td><td><span className="pill neutral">{TYPE_LABEL[e.type] || e.type}</span></td>
            <td>{[e.description, e.mode?.toUpperCase(), e.ref_no].filter(Boolean).join(" · ")}</td>
            <td>{money(e.amount)}</td><td>{e.tax ? money(e.tax) : "—"}</td><td><b>{["payment", "advance", "discount"].includes(e.type) || e.total < 0 ? "−" : ""}{money(Math.abs(e.total))}</b></td>
            <td>{e.source_type === "manual" && e.status !== "voided" && !settled && <button className="icon-button" onClick={() => voidIt(e)} data-testid={`folio-void-${e.id}`}><Ban size={13} /></button>}</td>
          </tr>
        ))}
        {!(doc.folio || []).length && <tr><td colSpan={7}><div className="empty small">No folio entries yet.</div></td></tr>}
      </tbody></table></div>
      <div className="recipe-costing-summary">
        <div><small>{s.contract_total != null ? "Contract (incl. GST)" : s.stay_total != null ? "Stay total (incl. GST)" : "Charges"}</small><b data-testid="folio-charges">{money(s.contract_total ?? s.stay_total ?? s.charges)}</b>{s.stay_total != null && <small>posted so far {money(s.charges)}</small>}</div>
        <div><small>Paid</small><b data-testid="folio-paid">{money(s.payments)}</b></div>
        <div><small>Refunds / discounts</small><b>{money(s.refunds)} / {money(s.discounts)}</b></div>
        <div className={s.balance > 0 ? "warn hilight" : "good hilight"}><small>{s.balance < 0 ? "Credit to refund" : "Balance due"}</small><b data-testid="folio-balance">{money(Math.abs(s.balance))}</b>{s.advance_held > 0 && <small data-testid="folio-advance-held">advance held {money(s.advance_held)}</small>}{s.checkout_due != null && <small data-testid="folio-checkout-due">due if checking out today {money(s.checkout_due)}</small>}</div>
      </div>
      <Err err={a.err} testid="folio-error" />
      <ExportBar testid="folio" getDoc={() => folioDoc(doc)} />{" "}
      <button className="secondary-button" onClick={() => setAdding(!adding)} data-testid="folio-add-entry">{adding ? "Close" : settled ? "Post refund" : "Payment / charge / refund"}</button>
      {adding && <EntryForm postUrl={postUrl} settled={settled} onDone={() => { setAdding(false); onChanged(); }} />}
    </div>
  );
}

export function useLoad(fn, deps) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = () => fn().then((d) => { setData(d); setErr(""); }).catch((e) => setErr(errorMessage(e)));
  useEffect(() => { load(); }, deps); // eslint-disable-line
  return [data, load, err];
}
